import { ListFilter, X } from 'lucide-react';
import { useId, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  ICS204_ICON_BTN,
  ICS204_LABEL,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
} from './assignmentCardStyles';

export type FilterColumn = {
  key: string;
  label: string;
  options: string[];
};

type AssignmentListFiltersProps = {
  columns: FilterColumn[];
  /** Values checked per column key. An absent or empty entry means unfiltered. */
  selected: Record<string, string[]>;
  onToggle: (columnKey: string, value: string) => void;
  onClearAll: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Single entry point for filtering the ICS-204 list. Conditions stack: values
 * checked within a column are OR'd, and columns are AND'd together.
 */
const AssignmentListFilters: React.FC<AssignmentListFiltersProps> = ({
  columns,
  selected,
  onToggle,
  onClearAll,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search assignments…',
}) => {
  const idPrefix = useId();

  const activeConditions = useMemo(
    () =>
      columns.flatMap((column) =>
        (selected[column.key] ?? []).map((value) => ({
          columnKey: column.key,
          columnLabel: column.label,
          value,
        }))
      ),
    [columns, selected]
  );

  return (
    // `dark` opts the shadcn primitives into the dark token set so they read
    // against the IAP panel; see assignmentCardStyles.
    <div
      className="dark tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2"
      data-testid="ics204-filters"
    >
      <Popover>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={ICS204_OUTLINE_BTN}
              data-testid="ics204-filter-trigger"
            >
              <ListFilter />
              Filter
              {activeConditions.length > 0 && (
                <Badge variant="count" data-testid="ics204-filter-count">
                  {activeConditions.length}
                </Badge>
              )}
            </Button>
          }
        />
        {/* The popup portals to the body, escaping the `dark` ancestor above,
            so it has to declare the token set itself. */}
        <PopoverContent
          className="dark tw:w-80 tw:gap-3 tw:p-3"
          align="start"
          data-testid="ics204-filter-panel"
        >
          <div className="tw:flex tw:max-h-80 tw:flex-col tw:gap-4 tw:overflow-y-auto">
            {columns.map((column) => (
              <div
                key={column.key}
                className="tw:flex tw:flex-col tw:gap-2"
                data-testid={`ics204-filter-group-${column.key}`}
              >
                <div
                  className={cn(
                    ICS204_LABEL,
                    'tw:font-semibold tw:tracking-wide tw:uppercase'
                  )}
                >
                  {column.label}
                </div>
                {column.options.length === 0 ? (
                  <div className={ICS204_MUTED}>No values</div>
                ) : (
                  column.options.map((option) => {
                    const optionId = `${idPrefix}-${column.key}-${slugify(option)}`;
                    return (
                      <div
                        key={option}
                        className="tw:flex tw:items-center tw:gap-2"
                      >
                        <Checkbox
                          id={optionId}
                          checked={(selected[column.key] ?? []).includes(
                            option
                          )}
                          onCheckedChange={() => onToggle(column.key, option)}
                          data-testid={`ics204-filter-option-${column.key}-${slugify(option)}`}
                        />
                        <Label htmlFor={optionId} className="tw:font-normal">
                          {option}
                        </Label>
                      </div>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Input
        type="search"
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={searchPlaceholder}
        data-testid="ics204-search-input"
        className="tw:max-w-xs tw:min-w-[12rem] tw:flex-1 tw:bg-black tw:text-foreground"
      />

      {activeConditions.map(({ columnKey, columnLabel, value }) => (
        <Badge
          key={`${columnKey}-${value}`}
          variant="outline"
          className="tw:h-6 tw:gap-1 tw:overflow-visible tw:pr-0.5"
          data-testid="ics204-filter-chip"
        >
          <span className={ICS204_MUTED}>{columnLabel}:</span>
          <span>{value}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Remove ${columnLabel} filter ${value}`}
            className={ICS204_ICON_BTN}
            onClick={() => onToggle(columnKey, value)}
          >
            <X />
          </Button>
        </Badge>
      ))}

      {activeConditions.length > 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={ICS204_ICON_BTN}
          data-testid="ics204-filter-clear-all"
          onClick={onClearAll}
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

export default AssignmentListFilters;
